// ===========================================
// Avatar Settings Tab
// Phase 2 Priority 11 - User Avatar Upload
// ===========================================
// Admin settings tab to configure which roles can upload avatars
//
// IMPORTANT: This is for USER photo uploads (Admin, Teacher, Parent).
// Students have a SEPARATE avatar system using character sprites.
// ===========================================

import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  TextField,
  InputAdornment,
  Button,
  Stack,
  Typography,
  Alert,
  Skeleton,
  Chip,
  Divider,
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as ResetIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import {
  useAdminAvatarSettings,
  useUpdateAvatarSettings,
  useResetAvatarSettings,
} from '../../../hooks/useAdminSettings';
import { UserRole } from '../../../types/avatar.types';
import { useRoleColorScheme } from '../../../hooks/useRoleColorScheme';
import SettingsWriteGate from './SettingsWriteGate';

// ===========================================
// TYPES
// ===========================================

interface AvatarSettingsFormData {
  allowAdmin: boolean;
  allowTeacher: boolean;
  allowParent: boolean;
  maxFileSizeMB: number;
}

// Role labels with descriptions
const ROLE_CONFIG: Record<Exclude<UserRole, 'STUDENT'>, { label: string; description: string }> = {
  ADMIN: { label: 'Administrators', description: 'School administrators' },
  TEACHER: { label: 'Teachers', description: 'Music teachers and instructors' },
  PARENT: { label: 'Parents', description: 'Parent and guardian accounts' },
};

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function AvatarSettingsTab() {
  const { data: settings, isLoading, error } = useAdminAvatarSettings();
  const roleScheme = useRoleColorScheme(); // the school's role colours (#629)
  const updateMutation = useUpdateAvatarSettings();
  const resetMutation = useResetAvatarSettings();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isDirty },
  } = useForm<AvatarSettingsFormData>({
    defaultValues: {
      allowAdmin: true,
      allowTeacher: true,
      allowParent: false,
      maxFileSizeMB: 5,
    },
  });

  const allowAdmin = watch('allowAdmin');
  const allowTeacher = watch('allowTeacher');
  const allowParent = watch('allowParent');

  // Update form when data loads
  useEffect(() => {
    if (settings) {
      reset({
        allowAdmin: settings.allowedRoles.includes('ADMIN'),
        allowTeacher: settings.allowedRoles.includes('TEACHER'),
        allowParent: settings.allowedRoles.includes('PARENT'),
        maxFileSizeMB: settings.maxFileSizeMB,
      });
    }
  }, [settings, reset]);

  const onSubmit = (data: AvatarSettingsFormData) => {
    const allowedRoles: UserRole[] = [];
    if (data.allowAdmin) allowedRoles.push('ADMIN');
    if (data.allowTeacher) allowedRoles.push('TEACHER');
    if (data.allowParent) allowedRoles.push('PARENT');

    updateMutation.mutate({
      allowedRoles,
      maxFileSizeMB: data.maxFileSizeMB,
    });
  };

  const handleReset = () => {
    if (window.confirm('Reset avatar settings to defaults? (Admin + Teacher only, 5MB max)')) {
      resetMutation.mutate();
    }
  };

  // Build list of currently allowed roles for preview
  const allowedRolesList: UserRole[] = [];
  if (allowAdmin) allowedRolesList.push('ADMIN');
  if (allowTeacher) allowedRolesList.push('TEACHER');
  if (allowParent) allowedRolesList.push('PARENT');

  if (isLoading) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={150} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load avatar settings. Please try again.
      </Alert>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        {/* Info Alert */}
        <Alert severity="info" icon={<PersonIcon />}>
          <Typography variant="body2">
            <strong>Note:</strong> These settings control <strong>user photo uploads</strong> for
            Admin, Teacher, and Parent accounts. Students have a separate avatar system using
            character sprites managed through the gamification system.
          </Typography>
        </Alert>

        {/* Role Permissions Section */}
        <Card variant="outlined">
          <CardHeader
            title="Avatar Upload Permissions"
            subheader="Select which user roles can upload their own avatar photo"
          />
          <CardContent>
            <FormControl component="fieldset">
              <FormLabel component="legend">Allowed Roles</FormLabel>
              <FormGroup>
                {(Object.keys(ROLE_CONFIG) as Exclude<UserRole, 'STUDENT'>[]).map((role) => {
                  const config = ROLE_CONFIG[role];
                  const colors = roleScheme[role];
                  const fieldName = `allow${role.charAt(0) + role.slice(1).toLowerCase()}` as
                    | 'allowAdmin'
                    | 'allowTeacher'
                    | 'allowParent';

                  return (
                    <Controller
                      key={role}
                      name={fieldName}
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              {...field}
                              checked={field.value}
                              sx={{
                                color: colors.background,
                                '&.Mui-checked': {
                                  color: colors.background,
                                },
                              }}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body1">{config.label}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {config.description}
                              </Typography>
                            </Box>
                          }
                          sx={{ alignItems: 'flex-start', mb: 1 }}
                        />
                      )}
                    />
                  );
                })}
              </FormGroup>
            </FormControl>

            <Divider sx={{ my: 2 }} />

            {/* Preview of allowed roles */}
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Users who can upload avatars:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {allowedRolesList.length > 0 ? (
                  allowedRolesList.map((role) => (
                    <Chip
                      key={role}
                      label={ROLE_CONFIG[role as Exclude<UserRole, 'STUDENT'>]?.label || role}
                      size="small"
                      sx={{
                        bgcolor: roleScheme[role].background,
                        color: roleScheme[role].text,
                      }}
                    />
                  ))
                ) : (
                  <Typography variant="body2" color="error">
                    No roles selected - avatar upload will be disabled for all users
                  </Typography>
                )}
              </Stack>
            </Box>
          </CardContent>
        </Card>

        {/* File Size Limits Section */}
        <Card variant="outlined">
          <CardHeader
            title="File Size Limits"
            subheader="Configure maximum file size for avatar uploads"
          />
          <CardContent>
            <Stack spacing={2}>
              <Controller
                name="maxFileSizeMB"
                control={control}
                rules={{
                  min: { value: 1, message: 'Minimum 1 MB' },
                  max: { value: 10, message: 'Maximum 10 MB' },
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    type="number"
                    label="Maximum File Size"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message || 'Recommended: 5 MB or less'}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">MB</InputAdornment>,
                    }}
                    inputProps={{ min: 1, max: 10, step: 1 }}
                    sx={{ width: 200 }}
                    onChange={(e) => field.onChange(parseInt(e.target.value) || 5)}
                  />
                )}
              />

              <Typography variant="body2" color="text.secondary">
                <strong>Supported formats:</strong> JPEG, PNG
                <br />
                <strong>Maximum dimensions:</strong> 2048 x 2048 pixels
                <br />
                <strong>Generated thumbnails:</strong> 32x32, 64x64, 128x128, 512x512
              </Typography>
            </Stack>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <SettingsWriteGate>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<ResetIcon />}
              onClick={handleReset}
              disabled={resetMutation.isPending}
            >
              Reset to Defaults
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={!isDirty || updateMutation.isPending}
            >
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </Stack>
        </SettingsWriteGate>
      </Stack>
    </Box>
  );
}
